import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  Grid,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Tooltip,
  Typography,
  MenuItem,
  TextField,
  Card,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { CustomAutocomplete, CustomDatePicker, CustomTextField, CustomTimePicker } from '../StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton, RedOutlinedButton } from '../Buttons';
import { EntitiesDialog } from '../EntitiesDialog';
import NewLocationDialog from './Dialogs/NewLocationDialog';
import StandHandlerDialog from './Dialogs/StandHandlerDialog';
import StandsCard from './Components/StandsCard';

import API from '../../api';
import { usePopup } from '../../context/PopupContext';
import { HttpErrorHandler } from '../ErrorHandler';
import DatesCard from './Components/DatesCard';
import { DecisionDialog } from '../DecisionDialog';
import EventFileCard from './Components/EventFileCard';
import IssuesCard from './Components/IssuesCard';
import GuestsCard from './Components/GuestsCard';

export default function EventsHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  const [redirectType, setRedirectType] = useState(location.state.redirectType);
  const startSelection = location.state.start;
  const endSelection = location.state.end
    ? new Date(new Date(location.state.end).setDate(new Date(location.state.end).getDate() - 1))
    : undefined;
  const [event, setEvent] = useState(location.state.event || {});
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [hasAuth, setHasAuth] = useState(false);
  const isAdmin = location.state.isAdmin || false;

  /* -------------------------------- Popup -------------------------------- */

  const { showPopup, setPopUpText } = usePopup();
  /* --------------------------------- Common --------------------------------- */

  //   Locations
  const [locations, setLocations] = useState([]);
  const [isLocationsFetching, setIsLocationsFetching] = useState(false);
  const [openNewLocationDialog, setOpenNewLocationDialog] = useState(false);
  const newLocationDialogRef = useRef();
  const locationsDebounceRef = useRef(null);

  const searchLocations = (search) => {
    clearTimeout(locationsDebounceRef.current);
    if (search.length < 3) {
      setLocations([]);
      return;
    }
    locationsDebounceRef.current = setTimeout(() => {
      setIsLocationsFetching(true);
      API.get('/locations', { params: { search } })
        .then((response) => {
          setLocations(response.data);
        })
        .finally(() => setIsLocationsFetching(false));
    }, 500);
  };

  const handleOpenNewLocationDialog = () => {
    setOpenNewLocationDialog(true);
  };

  const handleCloseNewLocationDialog = () => {
    setOpenNewLocationDialog(false);
  };

  const createLocation = (newLocationData) => {
    API.post('/locations', newLocationData)
      .then((response) => {
        setLocations([]);
        handleCloseNewLocationDialog();
        newLocationDialogRef.current.refreshNewLocationForm();

        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);

        showPopup(false);
      });
  };

  // Categories
  const [celebrationCategories, setCelebrationCategories] = useState([]);

  const getCelebrationCategories = () => {
    if (celebrationCategories && celebrationCategories.length === 0) {
      API.get('/celebration_categories').then((response) => {
        setCelebrationCategories(response.data);
      });
    }
  };

  const [eventCategories, setEventCategories] = useState([]);

  const getEventCategories = () => {
    if (eventCategories && eventCategories.length === 0) {
      API.get('/event_categories').then((response) => {
        setEventCategories(response.data);
      });
    }
  };

  // Celebrations
  const [celebrations, setCelebrations] = useState([]);

  const getCelebrations = () => {
    if (celebrations && celebrations.length === 0) {
      API.get('/event_celebrations').then((response) => {
        setCelebrations(response.data);
      });
    }
  };

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);

  const getCampaigns = () => {
    if (campaigns && campaigns.length === 0) {
      API.get('/thematic_campaigns').then((response) => {
        setCampaigns(response.data);
      });
    }
  };

  // modes
  const [eventModesList, setEventModesList] = useState([]);

  const getEventModesList = () => {
    if (eventModesList && eventModesList.length === 0) {
      API.get('/event_modes').then((response) => {
        setEventModesList(response.data);
      });
    }
  };

  // Dates
  const [dates, setDates] = useState([]);
  const [isDatesApiCalled, setIsDatesApiCalled] = useState(false);

  const getDates = () => {
    if (event && Object.keys(event).length > 0 && dates && dates.length === 0) {
      API.get(`/celebration_dates/${event.id}`).then((response) => {
        setDates(response.data);
        setIsDatesApiCalled(true);
      });
    } else {
      setIsDatesApiCalled(true);
    }
  };

  const refreshDates = (eventID = event.id) => {
    setIsDatesApiCalled(false);

    API.get(`/celebration_dates/${eventID}`)
      .then((response) => {
        setDates(response.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(setIsDatesApiCalled(true));
  };

  // Guests
  const [guests, setGuests] = useState([]);
  const [isGuestsApiCalled, setIsGuestsApiCalled] = useState(false);

  const getGuests = () => {
    if (event && Object.keys(event).length > 0 && guests && guests.length === 0) {
      API.get(`/event_guests/${event.id}`).then((response) => {
        setGuests(response.data);
        setIsGuestsApiCalled(true);
      });
    } else {
      setIsGuestsApiCalled(true);
    }
  };

  const refreshGuests = (eventID = event.id) => {
    setIsGuestsApiCalled(false);

    API.get(`/event_guests/${eventID}`)
      .then((response) => {
        setGuests(response.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(setIsGuestsApiCalled(true));
  };

  // issues
  const [issues, setIssues] = useState([]);
  const [isIssueApiCalled, setIsIssuesApiCalled] = useState(false);

  const getIssues = () => {
    if (event && Object.keys(event).length > 0 && issues && issues.length === 0) {
      API.get(`/event_issues/${event.id}`).then((response) => {
        setIssues(response.data);
        setIsIssuesApiCalled(true);
      });
    } else {
      setIsIssuesApiCalled(true);
    }
  };

  // const refreshIssues = (eventID = event.id) => {
  //   setIsIssuesApiCalled(false);

  //   API.get(`/event_issues/${eventID}`)
  //     .then((response) => {
  //       setIssues(response.data);
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //     })
  //     .finally(setIsIssuesApiCalled(true));
  // };

  // Stands
  const [stands, setStands] = useState([]);
  const [isStandApiCalled, setIsStandApiCalled] = useState(false);
  const [openStandHandlerDialog, setOpenStandHandlerDialog] = useState(false);
  const [standHandlerType, setStandHandlerType] = useState('create');
  const [standToHandle, setStandToHandle] = useState(null);
  const standHandlerRef = useRef();

  const getStands = () => {
    if (event && Object.keys(event).length > 0 && stands && stands.length === 0) {
      API.get(`/celebration_stands/${event?.id}`).then((response) => {
        setStands(response.data);
        setIsStandApiCalled(true);
      });
    } else {
      setIsStandApiCalled(true);
    }
  };

  const refreshStands = () => {
    setIsStandApiCalled(false);

    API.get(`/celebration_stands/${event?.id}`).then((response) => {
      setStands(response.data);
      setIsStandApiCalled(true);
    });
  };

  const handleOpenStandHandlerDialog = (stand, type) => {
    setStandToHandle(stand);
    setStandHandlerType(type);
    setOpenStandHandlerDialog(true);
  };

  const handleCloseStandHandlerDialog = () => {
    setOpenStandHandlerDialog(false);
  };

  const createStand = (newStandData) => {
    API.post('/celebration_stands', newStandData)
      .then((response) => {
        refreshStands();
        handleCloseStandHandlerDialog();
        standHandlerRef.current.refreshStandForm();
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const updateStand = (updatedStandData) => {
    API.put(`/celebration_stands/${standToHandle.id}`, updatedStandData)
      .then((response) => {
        refreshStands();
        handleCloseStandHandlerDialog();
        standHandlerRef.current.refreshStandForm();
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const deleteStand = (standId) => {
    API.delete(`/celebration_stands/${standId}`)
      .then((response) => {
        refreshStands();
        handleCloseStandHandlerDialog();
        standHandlerRef.current.refreshStandForm();
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* -------------------------------- New Event ------------------------------- */

  const [eventType, setEventType] = useState(location?.state?.event?.type || location?.state.creationType || 'event');
  const [eventTitle, setEventTitle] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventHours, setEventHours] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventEntity, setEventEntity] = useState('');
  const [eventLocation, setEventLocation] = useState({});
  const [eventCategory, setEventCategory] = useState({});
  const [eventStatus, setEventStatus] = useState('draft');
  const [eventExtLink, setEventExtLink] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventMeetingLink, setEventMeetingLink] = useState('');
  const [eventCelebration, setEventCelebration] = useState({});
  const [eventMode, setEventMode] = useState({});
  const [eventCampaign, setEventCampaign] = useState({});

  useEffect(() => {
    if (eventCategory && !!eventCategory.autocomplete_event_name) {
      setEventTitle(eventCategory.name);
    }
  }, [eventCategory]);

  function onSetEventStartTime(time) {
    setEventStartTime(time);
    const end = dayjs(time).add(1, 'hour');
    setEventEndTime(end);
  }

  useEffect(() => {
    const validDate =
      eventStartDate && dayjs(eventStartDate).isValid() && dayjs(eventStartDate).year().toString().length === 4;

    if (validDate && (!eventEndDate || !dayjs(eventEndDate).isValid())) {
      setEventEndDate(eventStartDate);
    }
  }, [eventStartDate, eventEndDate]);

  useEffect(() => {
    if (eventType === 'event') {
      setEventCelebration({});
    }

    if (eventType === 'celebration') {
      setEventStartTime(null);
      setEventEndTime(null);
      setEventMode('');
      setEventCampaign({});
      setEventMeetingLink('');
    }

    setErrors((prev) => ({
      ...prev,
      eventStartTime: false,
      eventEndTime: false,
      eventMode: false,
      eventCampaign: false,
      eventMeetingLink: false,
      eventCelebration: false,
    }));
  }, [eventType]);

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const [errors, setErrors] = useState({
    eventTitle: false,
    eventStartDate: false,
    eventEndDate: false,
    eventEntity: false,
    eventStartTime: false,
    eventEndTime: false,
    eventDateRangeOrder: false,
    eventDateRangeTooLong: false,
    eventExtLink: false,
    eventLocation: false,
  });
  const [descriptionError, setDescriptionError] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const getSelectedEntity = (selectedEntity) => {
    setEventEntity(selectedEntity);
  };

  const getAuthEntity = () => {
    API.get('event_entity').then((response) => {
      setEventEntity(response.data);
    });
  };

  const checkFormErrors = () => {
    const startDate = eventStartDate && Object.keys(eventStartDate).length > 0 ? eventStartDate : startSelection;
    const endDate = eventEndDate && Object.keys(eventEndDate).length > 0 ? eventEndDate : endSelection;

    const newErrors = {
      eventTitle: !eventTitle.trim(),
      eventDescription: eventDescription?.length > 3000,
      eventStartDate: !startDate,
      eventEndDate: !endDate,
      eventEntity: !eventEntity,
      eventLocation:
        !eventCelebration || Object.keys(eventCelebration).length === 0
          ? !eventLocation || Object.keys(eventLocation).length === 0
          : false,
      eventExtLink: eventExtLink && eventExtLink.trim() ? !eventExtLink.trim().toLowerCase().startsWith('http') : false,
      eventStartTime: false,
      eventEndTime: false,
      eventMeetingLink: false,
      eventCelebration: false,
      eventMode: false,
      eventCategory: false,
      eventDateRangeOrder: false,
      eventDateRangeTooLong: false,
    };

    if (eventType === 'event') {
      newErrors.eventStartTime = !eventStartTime;
      newErrors.eventEndTime = !eventEndTime;
      newErrors.eventMode = !eventMode;
      newErrors.eventCategory = !eventCategory || Object.keys(eventCategory).length === 0;
      if (eventMode?.mode === 'online') {
        newErrors.eventCelebration = false;
        newErrors.eventLocation = false;
      } else {
        const hasCelebration = eventCelebration && Object.keys(eventCelebration).length > 0;
        const hasLocation = eventLocation && Object.keys(eventLocation).length > 0;

        newErrors.eventCelebration = !hasCelebration && !hasLocation;
        newErrors.eventLocation = !hasLocation && !hasCelebration;
      }

      if (eventMode?.mode !== 'in-person') {
        newErrors.eventMeetingLink = !eventMeetingLink || !eventMeetingLink.trim();
      }
    }

    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      newErrors.eventDateRangeOrder = true;
    }

    if (eventStartTime && eventEndTime && startDate && endDate) {
      const startTimeObj = dayjs(eventStartTime);
      const endTimeObj = dayjs(eventEndTime);

      const startDateTime = dayjs(startDate)
        .hour(startTimeObj.hour())
        .minute(startTimeObj.minute())
        .second(startTimeObj.second());

      const endDateTime = dayjs(endDate)
        .hour(endTimeObj.hour())
        .minute(endTimeObj.minute())
        .second(endTimeObj.second());

      if (endDateTime.isBefore(startDateTime)) {
        newErrors.eventEndTime = true;
      }
    }

    const monthDiff = dayjs(endDate).diff(dayjs(startDate), 'month', true);
    if (monthDiff > 6) {
      newErrors.eventDateRangeTooLong = true;
    }

    setErrors(newErrors);

    return Object.values(newErrors).some((e) => e);
  };

  const createEvent = (e) => {
    e.preventDefault();

    const hasErrors = checkFormErrors();

    if (hasErrors) return;

    const startDate = eventStartDate && Object.keys(eventStartDate).length > 0 ? eventStartDate : startSelection;

    const endDate = eventEndDate && Object.keys(eventEndDate).length > 0 ? eventEndDate : endSelection;

    let data = {};

    if (eventType === 'celebration') {
      data = {
        title: eventTitle,
        description: eventDescription,
        status: eventStatus,
        entity_id: eventEntity && Object.keys(eventEntity).length !== 0 ? eventEntity.id : '',
        start_date: dayjs(startDate).format('YYYY/MM/DD'),
        end_date: dayjs(endDate).format('YYYY/MM/DD'),
        opening_hours: eventHours,
        category: eventCategory?.id,
        location_id: eventLocation?.id,
        external_link: eventExtLink,
        eventType,
      };
    } else {
      data = {
        title: eventTitle,
        description: eventDescription,
        status: eventStatus,
        entity_id: eventEntity && Object.keys(eventEntity).length !== 0 ? eventEntity.id : '',
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
        category: eventCategory?.id,
        external_link: eventExtLink,
        location_id: eventLocation?.id || null,
        event_mode_id: eventMode?.id,
        start_time: dayjs(eventStartTime).format('HH:mm:ss'),
        end_time: dayjs(eventEndTime).format('HH:mm:ss'),
        celebration_id: eventCelebration?.id || null,
        link_videocall: eventMeetingLink,
        thematic_campaign_id: eventCampaign?.id,
      };
    }

    const url = eventType === 'event' ? '/events' : '/celebrations';

    API.post(url, data)
      .then((response) => {
        if (eventType === 'event') {
          getEvent(response.data.data.id);
        } else {
          getCelebration(response.data.data.id);
        }
        showPopup(true, response.data.message);
        setEvent(response.data.data);
        setRedirectType('update');
        if (eventType === 'celebration') {
          refreshDates(response.data.data.id);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const updateEvent = (e) => {
    e.preventDefault();

    const hasErrors = checkFormErrors();

    if (hasErrors) return;

    let data = {};

    if (eventType === 'celebration') {
      data = {
        title: eventTitle,
        description: eventDescription,
        status: eventStatus,
        entity_id: eventEntity && Object.keys(eventEntity).length !== 0 ? eventEntity.id : '',
        start_date: dayjs(eventStartDate).format('YYYY-MM-DD'),
        end_date: dayjs(eventEndDate).format('YYYY-MM-DD'),
        opening_hours: eventHours,
        category: eventCategory?.id,
        external_link: eventExtLink,
        location_id: eventLocation?.id,
      };
    } else {
      data = {
        title: eventTitle,
        description: eventDescription,
        status: eventStatus,
        entity_id: eventEntity && Object.keys(eventEntity).length !== 0 ? eventEntity.id : '',
        start_date: dayjs(eventStartDate).format('YYYY-MM-DD'),
        end_date: dayjs(eventEndDate).format('YYYY-MM-DD'),
        category: eventCategory?.id,
        external_link: eventExtLink,
        location_id: eventLocation?.id || null,
        event_mode_id: eventMode?.id,
        start_time: dayjs(eventStartTime).format('HH:mm:ss'),
        end_time: dayjs(eventEndTime).format('HH:mm:ss'),
        celebration_id: eventCelebration?.id || null,
        link_videocall: eventMeetingLink,
        thematic_campaign_id: eventCampaign?.id,
      };
    }

    console.log(data);
    const url = eventType === 'event' ? `/events/${event.id}` : `/celebrations/${event.id}`;

    API.put(url, data)
      .then((response) => {
        // getEvents()
        // refreshEventForm()
        showPopup(true, response.data.message);
        setEvent(response.data.data);
        if (eventType === 'celebration') {
          refreshDates(response.data.data.id);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------ Delete Event ------------------------------ */

  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState({});

  // open delete calendar event dialog
  const handleOpenDeleteEventDialog = (event) => {
    setEventToDelete(event);
    setOpenDeleteEventDialog(true);
  };

  // close delete calendar event dialog
  const handleCloseDeleteEventDialog = () => {
    setEventToDelete({});
    setOpenDeleteEventDialog(false);
  };

  // Delete an event request
  const deleteEvent = () => {
    const eventType = event.type;

    const url = eventType === 'event' ? `/events/${event.id}` : `/celebrations/${event.id}`;

    API.delete(url)
      .then((response) => {
        handleCloseDeleteEventDialog();

        navigate('/admin/events');

        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const getCelebration = (celebrationId) => {
    API.get(`/celebrations/${celebrationId}`).then((response) => {
      if (response.data.authType === 'W') {
        setHasAuth(true);
      }
      setIsEventLoading(false);
      setEvent(response.data);
      setRedirectType('show');
      setEventType(response.data.type);
      setEventTitle(response.data.title);
      setEventDescription(response.data.description);
      setEventStartDate(response.data.start_date);
      setEventEndDate(response.data.end_date);
      setEventEntity(response.data.entity);
      setEventLocation(response.data.location);
      setEventCategory(response.data.category_relation);
      setEventExtLink(response.data.external_link);
      setEventHours(response.data.opening_hours);
      setEventStatus(response.data.status);
    });
  };

  const getEvent = (eventId) => {
    API.get(`/events/${eventId}`).then((response) => {
      if (response.data.authType === 'W') {
        setHasAuth(true);
      }
      setIsEventLoading(false);
      setEvent(response.data);
      setRedirectType('show');
      setEventType(response.data.type);
      setEventTitle(response.data.title);
      setEventDescription(response.data.description);
      setEventStartDate(response.data.start_date);
      setEventEndDate(response.data.end_date);
      setEventStartTime(dayjs(response.data.start_time, 'HH:mm'));
      setEventEndTime(dayjs(response.data.end_time, 'HH:mm'));
      setEventEntity(response.data.entity);
      setEventLocation(response.data.location || {});
      setEventCampaign(response.data.thematic_campaign);
      setEventMode(response.data.mode);
      setEventMeetingLink(response.data.link_videocall);
      setEventCelebration(response.data.celebration || {});
      setEventCategory(response.data.category_rel);
      setEventExtLink(response.data.external_link);
      setEventStatus(response.data.status);
    });
  };

  useEffect(() => {
    if (redirectType === 'update' || redirectType === 'show') {
      if (eventType === 'celebration') {
        getCelebration(event.id);
        getDates();
        getCelebrationCategories();
        getStands();
      } else {
        getEvent(event.id);
        getGuests();
        getIssues();
        getCampaigns();
        getCelebrations();
        getEventCategories();
        getEventModesList();
      }
    } else {
      if (eventType === 'celebration') {
        getCelebrationCategories();
        setIsDatesApiCalled(true);
        setIsStandApiCalled(true);
      } else {
        getCampaigns();
        getCelebrations();
        getEventCategories();
        getEventModesList();
        setIsGuestsApiCalled(true);
        setIsIssuesApiCalled(true);
      }
      setIsEventLoading(false);
      getAuthEntity();
    }
  }, [eventType]);

  useEffect(() => {
    setEventCategory({});
    setEventCampaign({});
    setEventLocation({});
    setEventCelebration({});
    setEventStartTime(null);
    setEventEndTime(null);
    setEventMeetingLink('');
    setEventMode({});
  }, [eventType]);

  return (
    <>
      <Grid container spacing={3} sx={{ padding: { xs: '12px', lg: '0' } }}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
            }}
          >
            <DefaultOutlinedButton
              customStyles={{ backgroundColor: 'white' }}
              startIcon={<ArrowBackIosIcon />}
              onClick={() => {
                const path = isAdmin ? '/admin/events' : '/user/events';
                navigate(path);
              }}
            >
              Indietro
            </DefaultOutlinedButton>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {/* Event */}
          <Card sx={{ padding: '24px', margin: '0 auto', height: isEventLoading ? '100%' : 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: '12px' }}>
              <Typography variant="h5">
                {redirectType === 'create'
                  ? 'Nuovo evento'
                  : redirectType === 'update'
                  ? 'Modifica evento'
                  : 'Dettaglio Evento'}
              </Typography>
            </Box>
            {isEventLoading ? (
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Event type */}
                <Grid item xs={12}>
                  <ToggleButtonGroup
                    sx={{ display: 'flex' }}
                    value={eventType}
                    exclusive
                    disabled={redirectType !== 'create'}
                    onChange={(e) => setEventType(e.currentTarget.value)}
                  >
                    <ToggleButton value="celebration" sx={{ flexGrow: '1' }}>
                      Festa
                    </ToggleButton>
                    <ToggleButton value="event" sx={{ flexGrow: '1' }}>
                      Evento
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12}>
                  <CustomAutocomplete
                    options={eventType === 'celebration' ? celebrationCategories : eventCategories}
                    readOnly={redirectType === 'show'}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name}
                    value={Object.keys(eventCategory || {}).length === 0 ? null : eventCategory}
                    onChange={(e, newValue) => setEventCategory(newValue || {})}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Categoria"
                        required={eventType === 'event'}
                        error={errors.eventCategory}
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: redirectType === 'show' ? null : params.InputProps.endAdornment,
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Event title */}
                <Grid item xs={12} sx={{ display: 'flex' }}>
                  <CustomTextField
                    value={eventTitle}
                    InputProps={{
                      readOnly: redirectType === 'show',
                    }}
                    label="Titolo"
                    required
                    error={errors.eventTitle}
                    onChange={(e) => setEventTitle(e.currentTarget.value)}
                    multiline
                  />
                </Grid>

                {/* Event description */}
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <CustomTextField
                    value={eventDescription}
                    InputProps={{
                      readOnly: redirectType === 'show',
                    }}
                    label="Descrizione"
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setEventDescription(value);
                      setDescriptionError(value.length > 3000);
                    }}
                    multiline
                    minRows={5}
                    error={descriptionError}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      color: descriptionError ? 'red' : 'text.secondary',
                      fontSize: '.8rem',
                      mt: 0.5,
                    }}
                  >
                    {eventDescription?.length ?? 0} / 3000
                  </Typography>
                </Grid>

                {eventType === 'event' && (
                  <Grid item xs={12}>
                    <CustomAutocomplete
                      options={campaigns}
                      readOnly={redirectType === 'show'}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      value={
                        eventCampaign && typeof eventCampaign === 'object' && Object.keys(eventCampaign).length > 0
                          ? eventCampaign
                          : null
                      }
                      onChange={(e, newValue) => setEventCampaign(newValue || {})}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Campagna"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: redirectType === 'show' ? null : params.InputProps.endAdornment,
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {eventType === 'event' && (
                  <Grid item xs={12} sx={{ display: 'flex' }}>
                    <CustomTextField
                      select
                      id="event-mode"
                      name="Modalità evento"
                      required
                      label="Modalità evento"
                      value={eventMode?.id || ''}
                      error={errors.eventMode}
                      InputProps={{ readOnly: redirectType === 'show' }}
                      SelectProps={{
                        IconComponent: redirectType === 'show' ? () => null : undefined,
                      }}
                      onChange={(e) => {
                        const selected = eventModesList.find((m) => m.id === e.target.value);
                        setEventMode(selected || null);
                      }}
                    >
                      {eventModesList.map((mode) => (
                        <MenuItem key={mode.id} value={mode.id}>
                          {' '}
                          {mode.description}
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Grid>
                )}

                {eventType === 'event' && eventMode?.mode !== 'in-person' && (
                  <Grid item xs={12}>
                    <CustomTextField
                      value={eventMeetingLink}
                      InputProps={{
                        readOnly: redirectType === 'show',
                      }}
                      required
                      label="Link Videocall"
                      error={errors.eventMeetingLink}
                      onChange={(e) => setEventMeetingLink(e.currentTarget.value)}
                    />
                  </Grid>
                )}

                <Grid item container spacing={3} xs={12}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker
                        id="event-start-date"
                        readOnly={redirectType === 'show'}
                        label="Data Inizio *"
                        format="DD/MM/YYYY"
                        error={errors.eventStartDate || errors.eventDateRangeOrder || errors.eventDateRangeTooLong}
                        value={startSelection ? dayjs(startSelection) : dayjs(eventStartDate)}
                        maxDate={
                          endSelection && endSelection !== 'Invalid Date' ? dayjs(endSelection) : dayjs(eventEndDate)
                        }
                        onChange={(date) => setEventStartDate(date)}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker
                        id="event-end-date"
                        readOnly={redirectType === 'show'}
                        format="DD/MM/YYYY"
                        label="Data Fine *"
                        error={errors.eventEndDate || errors.eventDateRangeOrder || errors.eventDateRangeTooLong}
                        value={
                          endSelection && endSelection !== 'Invalid Date' ? dayjs(endSelection) : dayjs(eventEndDate)
                        }
                        minDate={startSelection ? dayjs(startSelection) : dayjs(eventStartDate)}
                        onChange={(date) => setEventEndDate(date)}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {errors.eventDateRangeOrder && (
                    <Typography mx={3} pt={1} variant="span" sx={{ color: 'red', fontSize: '.8rem' }}>
                      La data di inizio non può essere antecedente alla data di fine
                    </Typography>
                  )}

                  {errors.eventDateRangeTooLong && (
                    <Typography mx={3} pt={1} variant="span" sx={{ color: 'red', fontSize: '.8rem' }}>
                      La durata massima è di 6 mesi
                    </Typography>
                  )}
                </Grid>

                {eventType === 'event' && (
                  <Grid item container spacing={3} xs={12}>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomTimePicker
                          id="event-start-time"
                          readOnly={redirectType === 'show'}
                          label="Ora Inizio *"
                          error={errors.eventStartTime}
                          value={eventStartTime}
                          onChange={(time) => onSetEventStartTime(time)}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomTimePicker
                          id="event-end-time"
                          readOnly={redirectType === 'show'}
                          label="Ora Fine *"
                          error={errors.eventEndTime}
                          value={eventEndTime}
                          onChange={(time) => setEventEndTime(time)}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {errors.eventEndTime && (
                      <Typography mx={3} pt={1} variant="span" sx={{ color: 'red', fontSize: '.8rem' }}>
                        L'orario di inizio non può essere antecedente all'orario di fine
                      </Typography>
                    )}
                  </Grid>
                )}

                {eventType === 'celebration' && (
                  <Grid item xs={12}>
                    <CustomTextField
                      value={eventHours}
                      InputProps={{
                        readOnly: redirectType === 'show',
                      }}
                      label="Descrizione Orari di Apertura"
                      onChange={(e) => setEventHours(e.currentTarget.value)}
                      multiline
                      minRows={1}
                    />
                  </Grid>
                )}

                {/* Event Entity */}
                <Grid item xs={12} sx={{ alignSelf: 'start' }}>
                  <CustomTextField
                    required
                    error={errors.eventEntity}
                    InputProps={{
                      readOnly: redirectType === 'show',

                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ cursor: 'pointer', display: redirectType === 'show' ? 'none' : 'flex' }}
                        >
                          <Tooltip title="Modifica" placement="top" arrow>
                            <EditIcon
                              onClick={() => {
                                setOpenEntitiesFilter(!openEntitiesFilter);
                              }}
                            />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    value={eventEntity ? eventEntity.name : ''}
                    label="Entità"
                  />

                  <EntitiesDialog
                    openDialog={openEntitiesFilter}
                    closeDialog={handleCloseEntitiesFilterDialog}
                    outputEntity={getSelectedEntity}
                    context={'userDetails'}
                  />
                </Grid>

                {eventType === 'event' && eventMode?.mode !== 'online' && (
                  <Grid item xs={12}>
                    <Box
                      component="fieldset"
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: 2,
                      }}
                    >
                      <Box component="legend" sx={{ px: 1, fontSize: '1rem', fontWeight: 600 }}>
                        Luogo evento
                      </Box>

                      {/* Celebration input */}
                      {Object.keys(eventLocation).length === 0 && (
                        <Grid item xs={12} sx={{ mb: 2 }}>
                          <CustomAutocomplete
                            options={celebrations}
                            readOnly={redirectType === 'show'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) =>
                              `${option.title} - ${dayjs(option.start_date).year()} ${
                                option?.location?.city?.name ? `- ${option?.location?.city?.name}` : ''
                              } ${option?.location?.province?.abbr ? option?.location?.province?.abbr : ''}` || ''
                            }
                            value={
                              eventCelebration && Object.keys(eventCelebration).length > 0 ? eventCelebration : null
                            }
                            onChange={(e, newValue) => setEventCelebration(newValue || {})}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Festa"
                                required
                                error={errors.eventCelebration}
                                variant="outlined"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: redirectType === 'show' ? null : params.InputProps.endAdornment,
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Location input */}
                      {Object.keys(eventCelebration).length === 0 && (
                        <Grid item container spacing={3} xs={12} alignItems="center">
                          <Grid item xs={12} md={redirectType !== 'show' ? 8.5 : 12}>
                            <CustomAutocomplete
                              options={locations}
                              readOnly={redirectType === 'show'}
                              loading={isLocationsFetching}
                              filterOptions={(x) => x}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              value={Object.keys(eventLocation).length === 0 ? null : eventLocation}
                              getOptionLabel={(option) => {
                                const cityName = option.city?.name || option.city_name || '';
                                return option.name ? `${option.name} (${option.street} - ${cityName})` : '';
                              }}
                              onInputChange={(e, value, reason) => {
                                if (reason === 'input') searchLocations(value);
                              }}
                              onChange={(e, newValue) => setEventLocation(newValue || {})}
                              noOptionsText="Digita almeno 3 caratteri per cercare"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Location"
                                  variant="outlined"
                                  error={errors.eventLocation}
                                  required
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: redirectType === 'show' ? null : params.InputProps.endAdornment,
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          {redirectType !== 'show' && (
                            <Grid item xs={12} md={3.5}>
                              <DefaultContainedButton
                                customStyles={{ whiteSpace: 'nowrap', width: '100%' }}
                                onClick={handleOpenNewLocationDialog}
                              >
                                Nuova Location
                              </DefaultContainedButton>
                            </Grid>
                          )}
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                )}

                {eventType !== 'event' && (
                  <Grid item xs={12}>
                    {Object.keys(eventCelebration).length === 0 && (
                      <Grid item container spacing={3} xs={12} alignItems="center">
                        <Grid item xs={12} md={redirectType !== 'show' ? 8.5 : 12}>
                          <CustomAutocomplete
                            options={locations}
                            readOnly={redirectType === 'show'}
                            loading={isLocationsFetching}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={Object.keys(eventLocation).length === 0 ? null : eventLocation}
                            getOptionLabel={(option) => {
                              const cityName = option.city?.name || option.city_name || '';
                              return option.name ? `${option.name} (${option.street} - ${cityName})` : '';
                            }}
                            onInputChange={(e, value, reason) => {
                              if (reason === 'input') searchLocations(value);
                            }}
                            onChange={(e, newValue) => setEventLocation(newValue || {})}
                            noOptionsText="Digita almeno 3 caratteri per cercare"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Location"
                                variant="outlined"
                                error={errors.eventLocation}
                                required
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: redirectType === 'show' ? null : params.InputProps.endAdornment,
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {redirectType !== 'show' && (
                          <Grid item xs={12} md={3.5}>
                            <DefaultContainedButton
                              customStyles={{ whiteSpace: 'nowrap', width: '100%' }}
                              onClick={handleOpenNewLocationDialog}
                            >
                              Nuova Location
                            </DefaultContainedButton>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Grid>
                )}

                <Grid item xs={12}>
                  <CustomTextField
                    value={eventExtLink}
                    InputProps={{
                      readOnly: redirectType === 'show',
                    }}
                    label="Link Esterno"
                    error={errors.eventExtLink}
                    onChange={(e) => setEventExtLink(e.currentTarget.value)}
                  />
                  <Typography
                    pt={1}
                    variant="span"
                    sx={{ color: errors.eventExtLink ? 'red' : 'inherit', fontSize: '.8rem' }}
                  >
                    Inserire un link in formato http://...
                  </Typography>
                </Grid>

                {/* Event status */}
                <Grid item xs={12} sx={{ display: 'flex' }}>
                  <CustomTextField
                    select
                    id="event-status"
                    InputProps={{
                      readOnly: redirectType === 'show',
                    }}
                    SelectProps={{
                      IconComponent: redirectType === 'show' ? () => null : undefined,
                    }}
                    name="Status"
                    required
                    label="Status"
                    value={eventStatus}
                    onChange={(e, value) => setEventStatus(e.target.value)}
                  >
                    <MenuItem value="draft">Bozza</MenuItem>
                    <MenuItem value="visible">Pubblico</MenuItem>
                  </CustomTextField>
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Box display="flex" gap={2}>
                    {redirectType === 'update' && (
                      <RedOutlinedButton onClick={handleOpenDeleteEventDialog}>Elimina</RedOutlinedButton>
                    )}

                    {redirectType !== 'show' && (
                      <DefaultContainedButton onClick={redirectType === 'create' ? createEvent : updateEvent}>
                        Salva
                      </DefaultContainedButton>
                    )}

                    {redirectType === 'show' && isAdmin && hasAuth && (
                      <DefaultContainedButton onClick={() => setRedirectType('update')}>
                        Modifica
                      </DefaultContainedButton>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Card>
        </Grid>

        {/* Locations and Stands */}
        <Grid item xs={12} md={6}>
          <EventFileCard
            event={event}
            setEvent={setEvent}
            showPopup={showPopup}
            setPopUpText={setPopUpText}
            canEdit={redirectType !== 'create' && hasAuth && isAdmin}
          />

          {eventType === 'celebration' && (
            <>
              <DatesCard
                dates={dates}
                isDatesApiCalled={isDatesApiCalled}
                disabledButton={!event || Object.keys(event).length === 0}
                refreshDatesList={refreshDates}
                canEdit={redirectType !== 'create' && hasAuth && isAdmin}
              />

              <StandsCard
                stands={stands}
                isStandApiCalled={isStandApiCalled}
                handleOpenStandHandlerDialog={handleOpenStandHandlerDialog}
                disabledButton={!event || Object.keys(event).length === 0 || !isStandApiCalled}
                canEdit={redirectType !== 'create' && hasAuth && isAdmin}
                refreshStandsList={refreshStands}
                showPopup={showPopup}
                setPopUpText={setPopUpText}
              />
            </>
          )}

          {eventType === 'event' && (
            <>
              <GuestsCard
                guests={guests}
                isGuestsApiCalled={isGuestsApiCalled}
                disabledButton={!event || Object.keys(event).length === 0}
                refreshGuestsList={refreshGuests}
                canEdit={redirectType !== 'create' && hasAuth && isAdmin}
                eventId={event?.id}
              />

              <IssuesCard
                issues={issues}
                canEdit={redirectType !== 'create' && hasAuth && isAdmin}
                disabledButton={!event || Object.keys(event).length === 0}
                eventId={event?.id}
              />
            </>
          )}
        </Grid>
      </Grid>

      <NewLocationDialog
        ref={newLocationDialogRef}
        openDialog={openNewLocationDialog}
        handleClose={handleCloseNewLocationDialog}
        createLocation={createLocation}
      />

      <StandHandlerDialog
        ref={standHandlerRef}
        openDialog={openStandHandlerDialog}
        handleClose={handleCloseStandHandlerDialog}
        createStand={createStand}
        updateStand={updateStand}
        deleteStand={deleteStand}
        handlerType={standHandlerType}
        standToHandle={standToHandle}
        event={event}
        showPopup={showPopup}
        setPopUpText={setPopUpText}
        refreshStandsList={refreshStands}
      />

      <DecisionDialog
        openDialog={openDeleteEventDialog}
        dialogTitle="Eliminazione evento"
        dialogDescription={
          <>
            Sei sicuro di voler eliminare l'evento {event.title}? <br />
            {eventType === 'celebration'
              ? 'Verranno eliminate anche tutte le date e gli stand ad esso relazionati.'
              : 'Verranno eliminati anche tutti gli ospiti e le materie ad esso relazionati.'}
          </>
        }
        closeOperation={handleCloseDeleteEventDialog}
        requestOperation={deleteEvent}
        requestText={'Elimina'}
        closeText={'Chiudi'}
        RequestButton={RedOutlinedButton}
      />

    </>
  );
}
